import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { getPageParams, PageRequest, SpringPage } from '@shared/page';
import { Contract } from '../models/contract';
import { ClusterRole, ReceivedClusterDelegation } from './organization-cluster.service';
import { OrganizationCodeOrId } from './admin-contract.service';
import { addHttpParams, buildHttpParams } from '@shared/utils/request-utils';
import { PageParams } from './logs/logs.service';
import { Feature } from '../../../config/feature-flags';
import { SiteMode } from '@shared/services/hostMode.service';
import { OrganizationBase, OrganizationInfo } from '@shared/services/organization.service';

@Injectable({
  providedIn: 'root',
})
export class AdminOrganizationService {
  constructor(private readonly http: HttpClient) {}

  getOrganization$(organizationCode: string): Observable<AdminOrganizationInfo> {
    return this.http.get<AdminOrganizationInfo>(`admin/organizations/${organizationCode}`);
  }

  async getOrganizations(query: OrganizationQuery, page = null): Promise<SpringPage<OrganizationListItem>> {
    return firstValueFrom(this.getOrganizations$(query, page));
  }

  getOrganizations$(query: OrganizationQuery, page = 0): Observable<SpringPage<OrganizationListItem>> {
    let params = new HttpParams().set('page', page);
    if (query?.searchTerm)
      params = params.appendAll({
        name: query.searchTerm,
        ownerEmail: query.searchTerm,
        ownerLastName: query.searchTerm,
        ownerFirstName: query.searchTerm,
        code: query.searchTerm,
      });
    if (query?.ownsSite) params = params.set('ownsSite', query.ownsSite);

    return this.http.get<SpringPage<OrganizationListItem>>(`admin/organizations`, { params });
  }

  public getOrganizationUsers(
    organizationRef: OrganizationCodeOrId,
    searchTerm = null,
    page: PageRequest = null,
  ): Observable<SpringPage<OrganizationUser>> {
    return this.http.get<SpringPage<OrganizationUser>>(`admin/organizations/${organizationRef}/users`, {
      params: addHttpParams({ email: searchTerm }, getPageParams(page)),
    });
  }

  async getOrganizationContracts(organizationCode: string, page: PageRequest = null): Promise<SpringPage<Contract>> {
    return firstValueFrom(
      this.http.get<SpringPage<Contract>>(`admin/organizations/${organizationCode}/contracts`, {
        params: getPageParams(page),
      }),
    );
  }

  async changeSiteOrganizationOwner(siteName: string, organizationCode: string): Promise<Object> {
    return firstValueFrom(this.http.put(`admin/sites/${siteName}/owner`, { code: organizationCode }));
  }

  deleteOrganization(organizationRef: OrganizationCodeOrId) {
    return this.http.delete(`admin/organizations/${organizationRef}`);
  }

  public getReceivedDelegations(organizationRef: OrganizationCodeOrId, pageParams: PageParams) {
    return this.http.get<SpringPage<ReceivedClusterDelegation>>(
      `admin/organizations/${organizationRef}/cluster-delegations/received`,
      { params: buildHttpParams(pageParams) },
    );
  }
}

interface OrganizationQuery {
  searchTerm?: string;
  ownsSite?: boolean;
}

export interface OrganizationListItem {
  id: number;
  code: string;
  name: string;
  street: string;
  zipCode: string;
  city: string;
  countryCode: string;
  owner: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
  clusterDelegations: {
    role: ClusterRole;
    cluster: {
      uid: string;
      name: string;
    };
  }[];

  createdAt: number;
  updatedAt: number;
  nbSites: number;
}

export type OrganizationUser = {
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
  };
  role: string;
};

export type AdminOrganizationInfo = OrganizationBase & OrganizationInfo & {
  featureFlags: Feature[];
  defaultSiteMode?: SiteMode;
}
